// EP-111 T111-9 (D9/D11) — the co-owner sidebar page. Reuses the
// Customers-list-style kit end to end (PageHeader primaryAction + one Card
// wrapping status Tabs + search + Recent/property/sort row + a
// SharedOwnerCard grid + Pagination) — see
// docs/ep-111-condominium-property-type.md D11 for the ASCII mockup and
// CustomerListPage for the pattern this parallels. Renamed from
// CoOwnerListPage by EP-133 SS4b-2 (2026-08-20) — "co-owner" stays the
// user-facing term throughout, only the internal naming moved.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserCheck, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, EmptyState, SetupGuide, Skeleton, Pagination, ConfirmDialog } from '@/components/ui'
import { SharedOwnerCard } from './components/SharedOwnerCard'
import { SharedOwnerFilters } from './components/SharedOwnerFilters'
import { SharedOwnerFormModal } from './components/SharedOwnerFormModal'
import { useOwnerContracts, useEndSharedOwner } from '@/hooks/useOwnerContracts'
import { useAllProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore, useNotificationStore } from '@/stores'
import { useListSort } from '@/hooks/useListSort'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useSetupScopeSession } from '@/hooks/useSupportSession'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import type { SharedOwnerLease } from '@/types'

export function SharedOwnerListPage() {
  // EP-160: a setup-scope support session may not create tenant data.
  const setupScope = useSetupScopeSession()
  const { t } = useTranslation('shared_owners')
  const { t: tc } = useTranslation('common')
  const translateError = useApiErrorTranslator('shared_owners')
  const addToast = useNotificationStore((s) => s.addToast)
  const { timezone } = useLocalizationConfig()

  const [page, setPage] = useState(1)
  const [usageStatus, setUsageStatus] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [formModal, setFormModal] = useState<{ open: boolean; sharedOwner: SharedOwnerLease | null }>({
    open: false,
    sharedOwner: null,
  })
  const [pendingEnd, setPendingEnd] = useState<SharedOwnerLease | null>(null)

  // Property scope — the global top-right picker (one source of truth, per
  // the designer's note in D11), same store CustomerListPage/other list pages
  // read from.
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, isFetching, error } = useOwnerContracts(
    page,
    usageStatus || undefined,
    debouncedSearch || undefined,
    selectedPropertyId ?? undefined,
    sortParams,
  )

  const { data: allProperties } = useAllProperties()
  // Co-owners only ever live on condominium properties — scope the picker
  // accordingly so it never offers a property that could never have one.
  const condoProperties = (allProperties ?? []).filter((p) => p.property_type === 'condominium')
  const propertyOptions = [
    { value: '', label: tc('labels.all_properties') },
    ...condoProperties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'started_at', label: tc('sort.by_start_date') },
    { value: 'unit_number', label: tc('sort.by_unit') },
  ]

  const sharedOwners = data?.data

  const endSharedOwner = useEndSharedOwner()

  const openAdd = () => setFormModal({ open: true, sharedOwner: null })
  const openEdit = (sharedOwner: SharedOwnerLease) => setFormModal({ open: true, sharedOwner })
  const closeForm = () => setFormModal({ open: false, sharedOwner: null })

  const confirmEnd = async () => {
    if (!pendingEnd) return
    try {
      await endSharedOwner.mutateAsync({ id: pendingEnd.id, ended_at: localToday(timezone) })
      addToast({ type: 'success', message: t('messages.ended') })
      setPendingEnd(null)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const hasAnyFilter = !!search || !!usageStatus

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          description={t('description')}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'add-co-owner',
            label: t('actions.add_co_owner'),
            icon: Plus,
            onClick: openAdd,
            hidden: setupScope,
          }}
        />

        <Card bodyClassName="space-y-6">
          <SharedOwnerFilters
            usageStatus={usageStatus}
            onUsageStatusChange={(v) => {
              setUsageStatus(v)
              setPage(1)
            }}
            search={search}
            onSearchChange={setSearch}
            propertyId={selectedPropertyId ? String(selectedPropertyId) : ''}
            onPropertyIdChange={(v) => {
              setSelectedPropertyId(v ? Number(v) : null)
              setPage(1)
            }}
            propertyOptions={propertyOptions}
            sort={sort}
            order={order}
            onSortChange={handleSort}
            onToggleOrder={handleToggleOrder}
            sortOptions={sortOptions}
          />

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-36" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              title={tc('errors.something_went_wrong')}
              description={tc('errors.something_went_wrong')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          ) : !sharedOwners || sharedOwners.length === 0 ? (
            !hasAnyFilter ? (
              <SetupGuide
                icon={<UserCheck className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                ]}
                action={
                  setupScope ? undefined : (
                    <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openAdd}>
                      {t('actions.add_co_owner')}
                    </Button>
                  )
                }
              />
            ) : (
              <EmptyState
                icon={<UserCheck className="w-12 h-12" />}
                title={t('empty.no_results')}
                description={t('empty.no_results')}
                action={
                  !search && !setupScope && (
                    <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openAdd}>
                      {t('actions.add_co_owner')}
                    </Button>
                  )
                }
              />
            )
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sharedOwners.map((sharedOwner) => (
                <SharedOwnerCard key={sharedOwner.id} sharedOwner={sharedOwner} onEdit={openEdit} onEnd={setPendingEnd} />
              ))}
            </div>
          )}
        </Card>

        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <SharedOwnerFormModal
        isOpen={formModal.open}
        onClose={closeForm}
        sharedOwner={formModal.sharedOwner}
        onSuccess={closeForm}
      />

      <ConfirmDialog
        isOpen={!!pendingEnd}
        onClose={() => setPendingEnd(null)}
        onConfirm={confirmEnd}
        title={t('messages.end_title')}
        message={pendingEnd ? t('messages.end_confirm', { name: pendingEnd.customer.name }) : ''}
        variant="warning"
        loading={endSharedOwner.isPending}
      />
    </>
  )
}
